import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { Globe, Search, RefreshCw, ShieldCheck, ShieldAlert, CheckCircle2, Clock } from 'lucide-react';

export const AdminDomainsView: React.FC = () => {
  const [domains, setDomains] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadDomains = async () => {
    setLoading(true);
    try {
      const data = await adminApiService.getDomains();
      if (data.success) {
        setDomains(data.domains || []);
      }
    } catch (err) {
      console.error('Failed to load domains:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDomains();
  }, []);

  const filtered = domains.filter(
    (d) =>
      d.domain?.toLowerCase().includes(search.toLowerCase()) ||
      d.website?.toLowerCase().includes(search.toLowerCase()) ||
      d.tenant?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <span>Domainler, SSL & DNS Yönetimi</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Müşteri web sitelerine bağlı özel alan adları (Custom Domain) ve Adiosoft alt alan adlarını (Subdomain) denetleyin.
          </p>
        </div>
        <button
          onClick={loadDomains}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Domain adı, web sitesi veya şirket adı ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Alan Adı</th>
                <th className="py-3 px-4">Web Sitesi</th>
                <th className="py-3 px-4">Şirket / Müşteri</th>
                <th className="py-3 px-4">SSL Güvenlik</th>
                <th className="py-3 px-4">Bağlantı Durumu</th>
                <th className="py-3 px-4">Bağlantı Tarihi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-500" />
                    Domainler yükleniyor...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    Kayıtlı domain bulunamadı.
                  </td>
                </tr>
              ) : (
                filtered.map((d, i) => (
                  <tr key={`${d.domain}-${i}`} className="hover:bg-slate-50/70 transition">
                    <td className="py-3.5 px-4 font-mono font-bold text-slate-900">
                      <a
                        href={`https://${d.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1.5"
                      >
                        {d.domain}
                      </a>
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-800">
                      {d.website}
                    </td>
                    <td className="py-3.5 px-4 text-slate-600">
                      {d.tenant}
                    </td>
                    <td className="py-3.5 px-4">
                      {d.ssl === 'Aktif' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                          <ShieldCheck className="w-3 h-3" />
                          SSL Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md">
                          <Clock className="w-3 h-3" />
                          {d.ssl}
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
                        {d.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px]">
                      {d.connectedDate}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
